import {
  collection,
  doc,
  query,
  orderBy,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  Timestamp,
  type DocumentData,
  type FirestoreDataConverter,
  type Query,
  type WithFieldValue,
} from 'firebase/firestore';

import { firestore } from './config';
import { USE_API, api, apiQuery } from '@/services/api/client';
import type { ProductoVenta, InsumoInterno } from '@/types/domain';

type ProductoBase = ProductoVenta | InsumoInterno;

const productoConverter = <T extends ProductoBase>(): FirestoreDataConverter<T> => ({
  toFirestore(data: WithFieldValue<T>): DocumentData {
    return data as DocumentData;
  },
  fromFirestore(snap, options) {
    const data = snap.data(options) as Omit<T, 'id'>;
    return { id: snap.id, ...data } as T;
  },
});

export const productosVentaCollection = collection(firestore, 'productos_venta').withConverter(
  productoConverter<ProductoVenta>(),
);

export const insumosInternosCollection = collection(firestore, 'insumos_internos').withConverter(
  productoConverter<InsumoInterno>(),
);

export const productosVentaQuery: any = USE_API
  ? apiQuery('/productos_venta', { _order: 'nombre' })
  : query(productosVentaCollection, orderBy('nombre'));
export const insumosInternosQuery: any = USE_API
  ? apiQuery('/insumos_internos', { _order: 'nombre' })
  : query(insumosInternosCollection, orderBy('nombre'));

export interface UpsertProductoVentaInput {
  id?: string;
  nombre: string;
  presentacion: string;
  categoria?: string;
  proveedorId?: string | null;
  proveedorNombre?: string | null;
  proveedorWhatsapp?: string | null;
  vencimiento?: Date | null;
  stock: number;
  stockMin: number;
  precioCompra: number;
  precioVenta: number;
  ubicacion?: string;
  notas?: string;
  fotoUrl?: string | null;
}

export async function upsertProductoVenta(input: UpsertProductoVentaInput) {
  const stockBajo = input.stock <= input.stockMin;
  if (USE_API) {
    const body = {
      ...input,
      stockBajo,
      vencimiento: input.vencimiento ? input.vencimiento.toISOString().slice(0, 10) : null,
    };
    if (input.id) return (await api.patch(`/productos_venta/${input.id}`, body)).id;
    return (await api.post('/productos_venta', body)).id;
  }
  const id = input.id ?? doc(productosVentaCollection).id;
  const ref = doc(productosVentaCollection, id);
  await setDoc(
    ref,
    {
      ...input,
      vencimiento: input.vencimiento ? Timestamp.fromDate(input.vencimiento) : null,
      stockBajo,
      updatedAt: serverTimestamp(),
      ...(input.id ? {} : { createdAt: serverTimestamp() }),
    } as any,
    { merge: true },
  );
  return id;
}

export interface UpsertInsumoInput {
  id?: string;
  nombre: string;
  presentacion: string;
  categoria?: string;
  proveedorId?: string | null;
  proveedorNombre?: string | null;
  proveedorWhatsapp?: string | null;
  vencimiento?: Date | null;
  stock: number;
  stockMin: number;
  ubicacion?: string;
  notas?: string;
  fotoUrl?: string | null;
}

export async function upsertInsumoInterno(input: UpsertInsumoInput) {
  const stockBajo = input.stock <= input.stockMin;
  if (USE_API) {
    const body = {
      ...input,
      stockBajo,
      vencimiento: input.vencimiento ? input.vencimiento.toISOString().slice(0, 10) : null,
    };
    if (input.id) return (await api.patch(`/insumos_internos/${input.id}`, body)).id;
    return (await api.post('/insumos_internos', body)).id;
  }
  const id = input.id ?? doc(insumosInternosCollection).id;
  const ref = doc(insumosInternosCollection, id);
  await setDoc(
    ref,
    {
      ...input,
      vencimiento: input.vencimiento ? Timestamp.fromDate(input.vencimiento) : null,
      stockBajo,
      updatedAt: serverTimestamp(),
      ...(input.id ? {} : { createdAt: serverTimestamp() }),
    } as any,
    { merge: true },
  );
  return id;
}

export async function ajustarStockVenta(id: string, nuevoStock: number, stockMin: number) {
  if (USE_API) {
    await api.patch(`/productos_venta/${id}`, { stock: nuevoStock, stockBajo: nuevoStock <= stockMin });
    return;
  }
  await updateDoc(doc(productosVentaCollection, id), {
    stock: nuevoStock,
    stockBajo: nuevoStock <= stockMin,
    updatedAt: serverTimestamp(),
  });
}

export async function ajustarStockInsumo(id: string, nuevoStock: number, stockMin: number) {
  if (USE_API) {
    await api.patch(`/insumos_internos/${id}`, { stock: nuevoStock, stockBajo: nuevoStock <= stockMin });
    return;
  }
  await updateDoc(doc(insumosInternosCollection, id), {
    stock: nuevoStock,
    stockBajo: nuevoStock <= stockMin,
    updatedAt: serverTimestamp(),
  });
}

export async function eliminarProductoVenta(id: string) {
  if (USE_API) return void (await api.delete(`/productos_venta/${id}`));
  await deleteDoc(doc(productosVentaCollection, id));
}

export async function eliminarInsumoInterno(id: string) {
  if (USE_API) return void (await api.delete(`/insumos_internos/${id}`));
  await deleteDoc(doc(insumosInternosCollection, id));
}
